// Autenticación de desarrollo: sin MSAL. Envía X-Dev-User / X-Dev-Name / X-Dev-Roles con el usuario
// elegido en el selector. Solo se activa con VITE_AUTH_MODE=dev.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { configurarAutenticacion } from '../api/http';
import { api } from '../api/cliente';
import { ContextoSesion, type Sesion } from './contexto';
import { USUARIOS_DEV } from './usuariosDev';
import { PantallaAcceso } from './PantallaAcceso';

const CLAVE = import.meta.env.VITE_DEMO ? 'upk-innova.demo.usuario' : 'upk-innova.usuario-dev';

/** En la demostración solo se ofrecen los tres perfiles de la instantánea. */
const USUARIOS_DISPONIBLES = import.meta.env.VITE_DEMO ? USUARIOS_DEV.filter((u) => ['dev-orion', 'dev-gerencia', 'dev-lector'].includes(u.oid)) : USUARIOS_DEV;

function usuarioInicial(): string {
  try {
    const hash = window.location.hash.includes('?') ? window.location.hash.slice(window.location.hash.indexOf('?')) : '';
    const url = new URLSearchParams(window.location.search).get('usuario') ?? new URLSearchParams(hash).get('usuario');
    if (url && USUARIOS_DISPONIBLES.some((u) => u.oid === url)) {
      localStorage.setItem(CLAVE, url);
      return url;
    }
    const guardado = localStorage.getItem(CLAVE);
    if (guardado && USUARIOS_DISPONIBLES.some((u) => u.oid === guardado)) return guardado;
  } catch {
    /* almacenamiento no disponible */
  }
  return USUARIOS_DISPONIBLES[0].oid;
}

/** Las cabeceras HTTP admiten ISO-8859-1 (tildes y eñes incluidas); otros caracteres se reemplazan. */
const cabeceraSegura = (s: string) => s.replace(/[^\x20-\x7e\u00a0-\u00ff]/g, '?');

export function DevAuth({ children }: { children: ReactNode }) {
  const [oid, setOid] = useState(usuarioInicial);
  const [activa, setActiva] = useState(true);
  const qc = useQueryClient();
  const actual = USUARIOS_DEV.find((u) => u.oid === oid) ?? USUARIOS_DEV[0];

  // El proveedor de cabeceras se registra antes del primer render de los hijos.
  configurarAutenticacion({
    cabeceras: async () => ({
      'X-Dev-User': actual.oid,
      'X-Dev-Name': cabeceraSegura(actual.nombre),
      'X-Dev-Roles': actual.roles.join(','),
      'X-Dev-Email': actual.correo,
    }),
    noAutorizado: () => undefined,
  });

  // GET /api/me registra al usuario de prueba en core.usuario (nombres visibles en listas y bitácora).
  useEffect(() => {
    api.yo().catch(() => undefined);
  }, [oid]);

  const cambiarUsuarioDev = useCallback(
    (nuevo: string) => {
      try {
        localStorage.setItem(CLAVE, nuevo);
      } catch {
        /* sin almacenamiento */
      }
      // Todo lo cargado con el rol anterior deja de ser válido.
      qc.clear();
      setOid(nuevo);
      setActiva(true);
    },
    [qc],
  );

  const sesion = useMemo<Sesion>(
    () => ({
      modo: 'dev',
      usuario: { oid: actual.oid, nombre: actual.nombre, correo: actual.correo, roles: actual.roles },
      roles: actual.roles,
      cerrarSesion: () => {
        qc.clear();
        setActiva(false);
      },
      usuariosDev: USUARIOS_DISPONIBLES,
      cambiarUsuarioDev,
    }),
    [actual, cambiarUsuarioDev, qc],
  );

  if (!activa) {
    return (
      <PantallaAcceso
        modo="dev"
        usuariosDev={USUARIOS_DISPONIBLES}
        alElegirUsuario={cambiarUsuarioDev}
      />
    );
  }

  return (
    <ContextoSesion.Provider value={sesion} key={oid}>
      {children}
    </ContextoSesion.Provider>
  );
}
